/**
 * Service for fetching chess ratings from Chess.com and Lichess
 */

const CHESSCOM_API_BASE = 'https://api.chess.com/pub';
const LICHESS_API_BASE = 'https://lichess.org/api';

/**
 * Fetch Chess.com ratings for a user
 * @param {string} username - Chess.com username
 * @returns {Promise<{blitz: number, rapid: number, classical: number}>}
 */
export const fetchChesscomRatings = async (username) => {
  try {
    console.log(`📊 Fetching Chess.com ratings for: ${username}`);
    
    const response = await fetch(`${CHESSCOM_API_BASE}/player/${username}/stats`);
    
    if (!response.ok) {
      throw new Error(`Chess.com API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Extract ratings from different time controls
    const ratings = {
      blitz: data.chess_blitz?.last?.rating || null,
      rapid: data.chess_rapid?.last?.rating || null,
      classical: data.chess_daily?.last?.rating || null, // Chess.com uses "daily" for classical
    };
    
    console.log('✅ Chess.com ratings fetched:', ratings);
    return ratings;
  } catch (error) {
    console.error('❌ Error fetching Chess.com ratings:', error);
    throw new Error('Failed to fetch Chess.com ratings. Please check the username.');
  }
};

/**
 * Fetch Lichess ratings for a user
 * @param {string} username - Lichess username
 * @returns {Promise<{blitz: number, rapid: number, classical: number}>}
 */
export const fetchLichessRatings = async (username) => {
  try {
    console.log(`📊 Fetching Lichess ratings for: ${username}`);
    
    const response = await fetch(`${LICHESS_API_BASE}/user/${username}`);
    
    if (!response.ok) {
      throw new Error(`Lichess API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Extract ratings from different time controls
    const ratings = {
      blitz: data.perfs?.blitz?.rating || null,
      rapid: data.perfs?.rapid?.rating || null,
      classical: data.perfs?.classical?.rating || null,
    };
    
    console.log('✅ Lichess ratings fetched:', ratings);
    return ratings;
  } catch (error) {
    console.error('❌ Error fetching Lichess ratings:', error);
    throw new Error('Failed to fetch Lichess ratings. Please check the username.');
  }
};

/**
 * Verify if a Chess.com username exists
 * @param {string} username - Chess.com username
 * @returns {Promise<boolean>}
 */
export const verifyChesscomUsername = async (username) => {
  try {
    const response = await fetch(`${CHESSCOM_API_BASE}/player/${username}`);
    return response.ok;
  } catch (error) {
    return false;
  }
};

/**
 * Verify if a Lichess username exists
 * @param {string} username - Lichess username
 * @returns {Promise<boolean>}
 */
export const verifyLichessUsername = async (username) => {
  try {
    const response = await fetch(`${LICHESS_API_BASE}/user/${username}`);
    return response.ok;
  } catch (error) {
    return false;
  }
};

